import Embed from '@editorjs/embed';
import Header from '@editorjs/header';
import InlineCode from '@editorjs/inline-code';
import Marker from '@editorjs/marker';
import Table from '@editorjs/table';
import Underline from '@editorjs/underline';

import { uploadFile } from '@/data/services/fileService';
import { ImageFolderEnum } from '@/models/CkEditor';
import { ImageToolTune } from '@/utils/editorjs/image-tune/ImageTune';
import RawTool from '@/utils/editorjs/raw-html/RawHtml';
import { convertBase64ToFile } from '@/utils/file';
import { BlockToolConstructable } from '@editorjs/editorjs';
import ImageTool from '@editorjs/image';
import List from '@editorjs/list';
import Paragraph from 'editorjs-paragraph-with-alignment';
import { useTranslation } from 'react-i18next';

type EditorTools = Omit<UploadByFile, 'file'>;

export type UploadByFile = {
  file: string | Blob;
  extraBody?: object;
  folder?: ImageFolderEnum;
  readOnly?: boolean;
};

export const useEditorJSTools = ({
  extraBody,
  folder = ImageFolderEnum.BOOK,
  readOnly,
}: EditorTools) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'editorJs',
  });
  async function uploadByFile(file: string | Blob) {
    const { image } = await uploadFile({
      file,
      folder,
      extraBody,
    });
    return {
      success: 1,
      file: {
        url: image,
      },
    };
  }

  async function uploadByUrl(url: string) {
    const file = convertBase64ToFile(url);
    return uploadByFile(file);
  }

  return {
    header: {
      class: Header,
      inlineToolbar: true,
      config: {
        placeholder: !readOnly ? t('placeholders.header') : undefined,
        levels: [1, 2, 3, 4, 5, 6],
        defaultLevel: 5,
      },
    },
    paragraph: {
      class: Paragraph,
      inlineToolbar: true,
      config: {
        placeholder: !readOnly ? t('placeholders.paragraph') : undefined,
        preserveBlank: true,
      },
    },

    image: {
      class: ImageTool,
      inlineToolbar: true,
      tunes: ['imageTune'],
      config: {
        uploader: {
          uploadByFile,
          uploadByUrl,
        },
      },
    },
    imageTune: {
      class: ImageToolTune as BlockToolConstructable,
      config: { resize: true },
    },
    underline: Underline,
    inlineCode: InlineCode,
    Marker,
    raw: {
      class: RawTool,
      config: {
        placeholder: t('placeholders.rawHtml'),
      },
    },
    embed: Embed,
    table: Table,
    nestedList: { class: List, inlineToolbar: true },
  };
};
